import React, { useState } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { FiPieChart, FiBarChart2, FiActivity, FiDollarSign, FiUser, FiClock, FiDownload } from 'react-icons/fi';

const AdminAnalytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('month');
  
  // Mock data for various metrics
  const userMetrics = {
    totalUsers: 12543,
    newUsers: 843,
    activeUsers: 7584,
    userGrowth: '+23%'
  };
  
  const financialMetrics = {
    totalRevenue: '$824,325',
    averageTransaction: '$67.23',
    conversionRate: '8.2%',
    revenueGrowth: '+15%'
  };
  
  const engagementMetrics = {
    averageSessionTime: '4m 32s',
    sessionPerUser: '3.5',
    returnRate: '68%',
    chatCompletion: '92%'
  };
  
  const deviceBreakdown = [
    { name: 'Mobile', value: 68 },
    { name: 'Desktop', value: 25 },
    { name: 'Tablet', value: 7 }
  ];
  
  const userActivity = [
    { day: 'Mon', users: 4500 },
    { day: 'Tue', users: 5200 },
    { day: 'Wed', users: 4800 },
    { day: 'Thu', users: 5800 },
    { day: 'Fri', users: 6000 },
    { day: 'Sat', users: 4300 },
    { day: 'Sun', users: 3900 }
  ];
  
  const topServices = [
    { name: 'Investment Advisory', users: 3524, growth: '+12%' },
    { name: 'Portfolio Management', users: 2854, growth: '+8%' },
    { name: 'Retirement Planning', users: 2215, growth: '+15%' },
    { name: 'Tax Planning', users: 1987, growth: '+5%' },
    { name: 'Estate Planning', users: 1654, growth: '+3%' }
  ];
  
  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-2xl font-bold">Analytics & Reports</h1>
          
          <div className="flex items-center space-x-2 mt-4 md:mt-0">
            <div className="bg-card border border-border rounded-lg p-1 flex">
              <button
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  timeRange === 'day' ? 'bg-primary text-white' : 'hover:bg-card-hover'
                }`}
                onClick={() => setTimeRange('day')}
              >
                Day
              </button>
              <button
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  timeRange === 'week' ? 'bg-primary text-white' : 'hover:bg-card-hover'
                }`}
                onClick={() => setTimeRange('week')}
              >
                Week
              </button>
              <button
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  timeRange === 'month' ? 'bg-primary text-white' : 'hover:bg-card-hover'
                }`}
                onClick={() => setTimeRange('month')}
              >
                Month
              </button>
              <button
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  timeRange === 'year' ? 'bg-primary text-white' : 'hover:bg-card-hover'
                }`}
                onClick={() => setTimeRange('year')}
              >
                Year
              </button>
            </div>
            
            <button className="bg-card border border-border rounded-lg p-2 hover:bg-card-hover">
              <FiDownload size={18} />
            </button>
          </div>
        </div>
        
        {/* Metrics Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* User Metrics */}
          <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold">User Metrics</h2>
              <div className="p-2 rounded-lg bg-primary/10">
                <FiUser size={18} />
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-text/60 text-sm">Total Users</p>
                  <h3 className="text-2xl font-bold mt-1">{userMetrics.totalUsers.toLocaleString()}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">New Users</p>
                  <h3 className="text-2xl font-bold mt-1">{userMetrics.newUsers.toLocaleString()}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">Active Users</p>
                  <h3 className="text-2xl font-bold mt-1">{userMetrics.activeUsers.toLocaleString()}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">Growth</p>
                  <h3 className="text-2xl font-bold mt-1 text-green-500">{userMetrics.userGrowth}</h3>
                </div>
              </div>
            </div>
          </div>
          
          {/* Financial Metrics */}
          <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold">Financial Metrics</h2>
              <div className="p-2 rounded-lg bg-primary/10">
                <FiDollarSign size={18} />
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-text/60 text-sm">Total Revenue</p>
                  <h3 className="text-2xl font-bold mt-1">{financialMetrics.totalRevenue}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">Avg. Transaction</p>
                  <h3 className="text-2xl font-bold mt-1">{financialMetrics.averageTransaction}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">Conversion Rate</p>
                  <h3 className="text-2xl font-bold mt-1">{financialMetrics.conversionRate}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">Growth</p>
                  <h3 className="text-2xl font-bold mt-1 text-green-500">{financialMetrics.revenueGrowth}</h3>
                </div>
              </div>
            </div>
          </div>
          
          {/* Engagement Metrics */}
          <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold">Engagement Metrics</h2>
              <div className="p-2 rounded-lg bg-primary/10">
                <FiClock size={18} />
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-text/60 text-sm">Avg. Session Time</p>
                  <h3 className="text-2xl font-bold mt-1">{engagementMetrics.averageSessionTime}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">Sessions/User</p>
                  <h3 className="text-2xl font-bold mt-1">{engagementMetrics.sessionPerUser}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">Return Rate</p>
                  <h3 className="text-2xl font-bold mt-1">{engagementMetrics.returnRate}</h3>
                </div>
                <div>
                  <p className="text-text/60 text-sm">Chat Completion</p>
                  <h3 className="text-2xl font-bold mt-1">{engagementMetrics.chatCompletion}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Charts & Detailed Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* User Activity Chart */}
          <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold">User Activity</h2>
              <div className="p-2 rounded-lg bg-primary/10">
                <FiActivity size={18} />
              </div>
            </div>
            
            <div className="p-6">
              <div className="h-80 flex items-center justify-center">
                {/* Placeholder for chart - In a real implementation, use a chart library */}
                <div className="w-full h-full bg-card/20 rounded-lg flex flex-col items-center justify-center">
                  <div className="w-full px-8 flex items-end justify-between h-[70%]">
                    {userActivity.map((day, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div 
                          className="w-8 bg-primary/80 rounded-t-md" 
                          style={{ height: `${(day.users / 6000) * 100}%` }}
                        ></div>
                        <p className="text-xs mt-2">{day.day}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-text/50 mt-6">Daily User Activity (Last Week)</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Device Breakdown */}
          <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold">Device Breakdown</h2>
              <div className="p-2 rounded-lg bg-primary/10">
                <FiPieChart size={18} />
              </div>
            </div>
            
            <div className="p-6">
              <div className="h-80 flex items-center justify-center">
                {/* Placeholder for pie chart - In a real implementation, use a chart library */}
                <div className="w-[250px] h-[250px] relative rounded-full bg-card/20 flex items-center justify-center overflow-hidden">
                  <div 
                    className="absolute inset-0 bg-blue-500" 
                    style={{ 
                      clipPath: 'polygon(50% 50%, 100% 0, 100% 100%, 0 100%, 0 0)',
                      opacity: 0.8 
                    }}
                  ></div>
                  <div 
                    className="absolute inset-0 bg-green-500" 
                    style={{ 
                      clipPath: 'polygon(50% 50%, 100% 0, 75% 0, 50% 25%, 25% 0, 0 0)',
                      opacity: 0.8 
                    }}
                  ></div>
                  <div 
                    className="absolute inset-0 bg-yellow-500" 
                    style={{ 
                      clipPath: 'polygon(50% 50%, 25% 0, 0 0, 0 25%)',
                      opacity: 0.8 
                    }}
                  ></div>
                  <div className="z-10 bg-foreground rounded-full w-[150px] h-[150px] flex items-center justify-center">
                    <p className="text-lg font-medium">Device Usage</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center mt-4 space-x-6">
                {deviceBreakdown.map((device, index) => (
                  <div key={index} className="flex items-center">
                    <div 
                      className={`w-3 h-3 rounded-full mr-2 ${
                        index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : 'bg-yellow-500'
                      }`}
                    ></div>
                    <span className="text-sm">{device.name}: {device.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Top Services Table */}
        <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="font-semibold">Top Services</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-card/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Growth</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Popularity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {topServices.map((service, index) => (
                  <tr key={index} className="hover:bg-card/30">
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {service.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {service.users.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-green-500">
                      {service.growth}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-full bg-card/30 rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full" 
                          style={{ width: `${(index === 0 ? 100 : 100 - (index * 15))}%` }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Report Generation */}
        <div className="bg-foreground border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold">Generate Reports</h2>
            <div className="p-2 rounded-lg bg-primary/10">
              <FiBarChart2 size={18} />
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button className="p-4 flex flex-col items-center justify-center bg-card/30 rounded-lg border border-border hover:bg-card/50 transition-colors">
                <FiUser size={24} className="mb-2" />
                <span className="font-medium">User Report</span>
              </button>
              
              <button className="p-4 flex flex-col items-center justify-center bg-card/30 rounded-lg border border-border hover:bg-card/50 transition-colors">
                <FiDollarSign size={24} className="mb-2" />
                <span className="font-medium">Financial Report</span>
              </button>
              
              <button className="p-4 flex flex-col items-center justify-center bg-card/30 rounded-lg border border-border hover:bg-card/50 transition-colors">
                <FiActivity size={24} className="mb-2" />
                <span className="font-medium">Activity Report</span>
              </button>
              
              <button className="p-4 flex flex-col items-center justify-center bg-card/30 rounded-lg border border-border hover:bg-card/50 transition-colors">
                <FiBarChart2 size={24} className="mb-2" />
                <span className="font-medium">Custom Report</span>
              </button>
            </div>
            
            <div className="mt-6 p-4 bg-card/20 rounded-lg border border-border/50 text-center">
              <p className="text-text/70 text-sm">
                Need a specific report? You can create custom reports with our advanced analytics tools.
              </p>
              <button className="mt-2 bg-primary text-white rounded-lg py-2 px-4 hover:bg-primary/90 transition-colors">
                Create Custom Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics; 